/**
 * TEMPORARY — RushMyFood (old) → First Delivery.
 *
 * From 2026-10-02 eHungry stopped sending its delivery orders to First Delivery.
 * Until that is fixed on their side, the backend sends them itself
 * (src/ehungry/fdDispatch.js): ASAP orders a few minutes after they arrive,
 * scheduled ones a set time before they are ready. This page is the switch for
 * that, and the place to send, cancel or hold back any one order by hand.
 *
 * A restaurant whose own drivers take some of its deliveries (say, everything
 * after 2 PM) gets a rule here, and those orders are left alone.
 *
 * Every decision is made on the server — the state of each row, why it cannot
 * go, and what a person must accept before sending it — so this page only
 * shows and asks. Remove it (and its route) when eHungry is sending again.
 */
import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Stack, Button,
  Switch, FormControlLabel, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Link, Autocomplete, RadioGroup, Radio, Divider,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'

import Header from '../components/Headers/Header'

const SETTINGS_FIELDS = `
  autoDispatch leadMinutes graceMinutes staleMinutes fdConfigured
  lastRunAt lastError updatedBy updatedAt
`

const ROW_FIELDS = `
  orderId remoteId externalId restaurantId restaurantName linkedRestaurantName fdMerchantId
  orderStatus placedAt readyAt scheduled dispatchAt
  customerName customerPhone deliveryAddress total tip paymentDescription
  ehungryCourierStatus ehungryCourierProvider
  state closedBeforeSend blocker restaurantDelivers confirmSend canSend canCancel canSkip canUnskip duplicate
  dispatch {
    status externalId fdOrderId attempts lastError sentAt sentBy fdStatus fdStatusAt
    dspName driverName driverPhone trackerLink dspTrackerLink pickupEta dropoffEta dropoffPhoto
    cancelledAt cancelledBy
  }
`

const RULE_FIELDS = `
  restaurantId restaurantName mode fromMinute startsAt untilAt timezone summary active deliversNow updatedBy updatedAt
`

const BOARD = gql`
  query AdminEhungryFdBoard($hours: Int) {
    adminEhungryFdBoard(hours: $hours) {
      settings { ${SETTINGS_FIELDS} }
      restaurantRules { ${RULE_FIELDS} }
      rows { ${ROW_FIELDS} }
    }
  }
`

const SET_RULE = gql`
  mutation EhungryFdSetRestaurantRule($restaurantId: ID!, $mode: String!, $fromMinute: Int) {
    ehungryFdSetRestaurantRule(restaurantId: $restaurantId, mode: $mode, fromMinute: $fromMinute) { ${RULE_FIELDS} }
  }
`

// The restaurants a rule can be for: those linked to an eHungry location.
const LOCATIONS = gql`
  query AdminEhungryLocationsForRules {
    adminEhungryLocations { _id restaurantId restaurantName }
  }
`

const SAVE_SETTINGS = gql`
  mutation EhungryFdSaveSettings($autoDispatch: Boolean, $leadMinutes: Int, $graceMinutes: Int) {
    ehungryFdSaveSettings(autoDispatch: $autoDispatch, leadMinutes: $leadMinutes, graceMinutes: $graceMinutes) {
      ${SETTINGS_FIELDS}
    }
  }
`

const SEND = gql`
  mutation EhungryFdSend($orderId: ID!, $force: Boolean) {
    ehungryFdSend(orderId: $orderId, force: $force) { ${ROW_FIELDS} }
  }
`

const CANCEL = gql`
  mutation EhungryFdCancel($orderId: ID!) {
    ehungryFdCancel(orderId: $orderId) { ${ROW_FIELDS} }
  }
`

const SKIP = gql`
  mutation EhungryFdSkip($orderId: ID!, $skip: Boolean!) {
    ehungryFdSkip(orderId: $orderId, skip: $skip) { ${ROW_FIELDS} }
  }
`

// Restaurants are in Massachusetts, and so are the people reading this, so
// times are pinned there rather than following whatever the browser says.
const TZ = 'America/New_York'
const dayKey = (d) => d.toLocaleDateString('en-US', { timeZone: TZ })
function when(s) {
  if (!s) return '—'
  const d = new Date(s)
  const time = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
  if (dayKey(d) === dayKey(new Date())) return time
  return `${d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })}, ${time}`
}
const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)

const FD_STATUS = {
  created: 'Finding a driver',
  pending: 'Finding a driver',
  runner_assigned: 'Driver assigned',
  en_route_pickup: 'Driver heading to the restaurant',
  arrived_pickup: 'Driver at the restaurant',
  picked_up: 'Picked up',
  en_route_dropoff: 'On the way to the customer',
  arrived_dropoff: 'At the customer',
  completed: 'Delivered',
  cancelled: 'Cancelled',
}
const fdStatusLabel = (s) => (s ? FD_STATUS[String(s).toLowerCase()] || s : 'Sent')

const TONE = {
  grey: { bgcolor: '#f3f4f6', color: '#374151' },
  blue: { bgcolor: '#e0e7ff', color: '#3730a3' },
  amber: { bgcolor: '#fef3c7', color: '#92400e' },
  red: { bgcolor: '#fee2e2', color: '#991b1b' },
  green: { bgcolor: '#dcfce7', color: '#166534' },
}

/** Chip text, tone and the line under it, for each server state. */
function describe(row, settings) {
  const d = row.dispatch || {}
  switch (row.state) {
    case 'WAITING':
      return { label: 'Waiting for eHungry', tone: 'blue', note: `Sends at ${when(row.dispatchAt)} unless eHungry sends it first` }
    case 'SCHEDULED':
      return { label: 'Scheduled', tone: 'blue', note: `Sends at ${when(row.dispatchAt)} (${settings.leadMinutes} min before ready)` }
    case 'DUE':
      return settings.autoDispatch
        ? { label: 'Sending now', tone: 'blue', note: 'Goes on the next check, within a minute' }
        : { label: 'Due', tone: 'amber', note: 'Auto-send is off. Send it by hand or turn auto-send on.' }
    case 'LATE':
      return { label: 'Ready time passed', tone: 'amber', note: `Over ${settings.staleMinutes} min past its ready time, so it is not sent automatically. Send it by hand if it still needs a driver.` }
    case 'HELD':
      return { label: 'Cash order', tone: 'amber', note: 'Not sent automatically. The First Delivery driver will not collect cash.' }
    case 'SELF':
      return { label: 'Restaurant delivers', tone: 'grey', note: `Own drivers: ${row.restaurantDelivers}.${row.orderStatus === 'completed' ? ' Completed on eHungry.' : ''} Not sent to First Delivery.` }
    case 'BLOCKED':
      return { label: "Can't send", tone: 'red', note: row.blocker }
    case 'EHUNGRY':
      return { label: 'eHungry sent it', tone: 'green', note: [row.ehungryCourierProvider, fdStatusLabel(row.ehungryCourierStatus)].filter(Boolean).join(' · ') }
    case 'CLOSED':
      if (row.closedBeforeSend) {
        return { label: 'Completed on eHungry', tone: 'amber', note: `Completed before its ${when(row.dispatchAt)} send time, so it was never sent` }
      }
      return { label: row.orderStatus === 'cancelled' ? 'Cancelled on eHungry' : 'Completed on eHungry', tone: 'grey', note: 'Never sent by us' }
    case 'SENDING':
      return { label: 'Sending…', tone: 'blue', note: null }
    case 'STUCK':
      return { label: "Send didn't finish", tone: 'red', note: `Check First Delivery for ${d.externalId || 'this order'} before sending again` }
    case 'RETRY':
      return { label: 'Retrying', tone: 'amber', note: `Attempt ${d.attempts} failed: ${d.lastError || 'unknown error'}` }
    case 'FAILED':
      return { label: 'Failed', tone: 'red', note: d.lastError }
    case 'SENT':
      return { label: fdStatusLabel(d.fdStatus), tone: 'green', note: null }
    case 'DELIVERED':
      return { label: 'Delivered', tone: 'green', note: null }
    case 'FD_CANCELLED':
      return { label: 'Cancelled by First Delivery', tone: 'red', note: 'Send it again if it still needs a driver' }
    case 'CANCELLED':
      return { label: 'Cancelled by us', tone: 'grey', note: `${d.cancelledBy || ''}${d.cancelledAt ? ` at ${when(d.cancelledAt)}` : ''}` }
    case 'SKIPPED':
      return { label: "Won't send", tone: 'grey', note: `Held back by ${d.sentBy || 'an admin'}` }
    default:
      return { label: row.state, tone: 'grey', note: null }
  }
}

const TABS = [
  { key: 'todo', label: 'To send', states: ['WAITING', 'SCHEDULED', 'DUE', 'LATE', 'HELD', 'BLOCKED', 'STUCK', 'RETRY', 'FAILED', 'FD_CANCELLED'] },
  { key: 'ours', label: 'Sent by us', states: ['SENDING', 'SENT', 'DELIVERED', 'CANCELLED'] },
  { key: 'ehungry', label: 'Sent by eHungry', states: ['EHUNGRY'] },
  { key: 'self', label: 'Restaurant delivers', states: ['SELF'] },
  { key: 'other', label: 'Closed / held', states: ['CLOSED', 'SKIPPED'] },
  { key: 'all', label: 'All', states: null },
]

// Rule times are local minutes after midnight (840 = 2:00 PM).
const toMinute = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number)
  return Number.isInteger(h) && Number.isInteger(m) ? h * 60 + m : null
}
const toHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
const clockOf = (min) => {
  const h = Math.floor(min / 60)
  return `${h % 12 === 0 ? 12 : h % 12}:${String(min % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

const RULE_MODES = [
  { value: 'DAILY', label: 'Every day', hint: 'From the time below until closing, every day. Earlier orders go to First Delivery.' },
  { value: 'TODAY', label: 'Only today', hint: 'From the time below until closing tonight. Tomorrow everything goes to First Delivery again.' },
  { value: 'ALWAYS', label: 'All orders', hint: 'Nothing from this restaurant goes to First Delivery until you remove this.' },
]

/** Add a restaurant's rule, or change one (`rule` set). */
function RuleDialog({ open, rule, rules, onClose, onSaved, onError }) {
  const [restaurant, setRestaurant] = useState(null) // { id, name }
  const [mode, setMode] = useState('DAILY')
  const [time, setTime] = useState('14:00')
  const { data, loading } = useQuery(LOCATIONS, { skip: !open || Boolean(rule) })
  const [setRule, { loading: saving }] = useMutation(SET_RULE)

  // `rule` is a snapshot taken when the dialog opened, so the board's polling
  // never resets the form mid-edit.
  useEffect(() => {
    if (!open) return
    setRestaurant(rule ? { id: rule.restaurantId, name: rule.restaurantName || `Restaurant #${rule.restaurantId}` } : null)
    setMode(rule && rule.active ? rule.mode : 'DAILY')
    setTime(toHHMM(rule && rule.fromMinute != null ? rule.fromMinute : 14 * 60))
  }, [open, rule])

  const options = useMemo(() => {
    const byId = new Map()
    for (const l of data?.adminEhungryLocations ?? []) {
      if (l.restaurantId && !byId.has(l.restaurantId)) {
        byId.set(l.restaurantId, { id: l.restaurantId, name: l.restaurantName || `Restaurant #${l.restaurantId}` })
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  const needsTime = mode !== 'ALWAYS'
  const fromMinute = needsTime ? toMinute(time) : null
  const replaces = !rule && restaurant ? rules.find((x) => x.restaurantId === restaurant.id) : null
  const valid = Boolean(restaurant) && (!needsTime || fromMinute != null)

  const save = () => setRule({ variables: { restaurantId: restaurant.id, mode, fromMinute } })
    .then(({ data: d }) => onSaved(`${restaurant.name}: own drivers, ${d.ehungryFdSetRestaurantRule.summary}`))
    .catch(onError)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {rule ? `When does ${restaurant ? restaurant.name : 'this restaurant'} deliver itself?` : 'Restaurant delivers its own orders'}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          {!rule && (
            <Autocomplete
              options={options}
              loading={loading}
              value={restaurant}
              onChange={(_, v) => setRestaurant(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} size="small" label="Restaurant" helperText="Restaurants linked on eHungry Locations" />}
            />
          )}
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            {RULE_MODES.map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio size="small" />}
                sx={{ alignItems: 'flex-start', mb: 0.5 }}
                label={(
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.hint}</Typography>
                  </Box>
                )}
              />
            ))}
          </RadioGroup>
          {needsTime && (
            <TextField
              size="small"
              type="time"
              label="Their drivers start at"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              inputProps={{ step: 300 }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            ASAP orders go by the time they were placed, scheduled orders by their ready time.
            Orders already with First Delivery stay there; cancel those on the board if needed.
          </Typography>
          {replaces && (
            <Alert severity="info">This replaces its current rule: {replaces.summary}.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Back</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save} sx={{ textTransform: 'none' }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EhungryFdDispatch = () => {
  const [tab, setTab] = useState('todo')
  const [snackbar, setSnackbar] = useState(null)
  const [confirm, setConfirm] = useState(null) // { title, body, run }
  const [ruleDialog, setRuleDialog] = useState(null) // { rule } — rule null to add one
  const [lead, setLead] = useState('')
  const [grace, setGrace] = useState('')

  // Polled: a scheduled order changes state on its own, and a driver's
  // progress arrives from First Delivery without anyone touching this page.
  const { data, loading, error, refetch } = useQuery(BOARD, {
    variables: { hours: 24 },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })
  const [saveSettings, saveState] = useMutation(SAVE_SETTINGS)
  const [send, sendState] = useMutation(SEND)
  const [cancel, cancelState] = useMutation(CANCEL)
  const [skip, skipState] = useMutation(SKIP)
  const [setRule, setRuleState] = useMutation(SET_RULE)
  const busy = sendState.loading || cancelState.loading || skipState.loading

  const settings = data?.adminEhungryFdBoard?.settings
  const rules = data?.adminEhungryFdBoard?.restaurantRules ?? []
  const rows = data?.adminEhungryFdBoard?.rows ?? []

  useEffect(() => {
    if (!settings) return
    setLead(String(settings.leadMinutes))
    setGrace(String(settings.graceMinutes))
  }, [settings?.leadMinutes, settings?.graceMinutes])

  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [
    t.key, t.states ? rows.filter((r) => t.states.includes(r.state)).length : rows.length,
  ])), [rows])
  const visible = useMemo(() => {
    const t = TABS.find((x) => x.key === tab)
    // A duplicate (ours and eHungry's both live) needs eyes whichever tab it is in.
    const list = t.states ? rows.filter((r) => t.states.includes(r.state) || (tab === 'todo' && r.duplicate)) : rows
    return list
  }, [rows, tab])

  const fail = (e) => setSnackbar({ severity: 'error', message: e.message })
  const done = (message) => { setSnackbar({ severity: 'success', message }); refetch() }

  const toggleAuto = (on) => {
    const run = () => saveSettings({ variables: { autoDispatch: on } })
      .then(() => done(on ? 'Auto-send is on' : 'Auto-send is off'))
      .catch(fail)
    if (!on) return run()
    setConfirm({
      title: 'Turn auto-send on?',
      body: `Every RushMyFood (old) delivery order that eHungry has not sent to First Delivery will be sent automatically: ASAP orders ${settings.graceMinutes} min after they arrive, scheduled orders ${settings.leadMinutes} min before their ready time. Orders whose ready time is more than ${settings.staleMinutes} min past, cash orders, and orders eHungry already sent are never sent automatically.`,
      action: 'Turn on',
      run,
    })
  }

  const saveTimes = () => {
    saveSettings({ variables: { leadMinutes: Number(lead), graceMinutes: Number(grace) } })
      .then(() => done('Timings saved'))
      .catch(fail)
  }

  const orderLabel = (r) => `#${r.externalId || r.remoteId} (${r.linkedRestaurantName || r.restaurantName || 'unknown restaurant'})`

  const askSend = (r) => setConfirm({
    title: `Send ${orderLabel(r)} to First Delivery?`,
    body: [
      `A First Delivery driver will be sent to ${r.linkedRestaurantName || r.restaurantName} for ${r.customerName || 'the customer'} at ${r.deliveryAddress}.`,
      r.confirmSend,
    ].filter(Boolean).join('\n\n'),
    warning: Boolean(r.confirmSend),
    action: 'Send now',
    run: () => send({ variables: { orderId: r.orderId, force: true } })
      .then(() => done(`${orderLabel(r)} sent to First Delivery`))
      .catch(fail),
  })

  const askCancel = (r) => setConfirm({
    title: `Cancel our First Delivery driver for ${orderLabel(r)}?`,
    body: 'First Delivery will call the driver off. eHungry is not told, so the order itself stays as it is there.',
    warning: true,
    action: 'Cancel driver',
    run: () => cancel({ variables: { orderId: r.orderId } })
      .then(() => done(`${orderLabel(r)} cancelled on First Delivery`))
      .catch(fail),
  })

  const doSkip = (r, on) => skip({ variables: { orderId: r.orderId, skip: on } })
    .then(() => done(on ? `${orderLabel(r)} will not be sent` : `${orderLabel(r)} can be sent again`))
    .catch(fail)

  const ruleName = (rule) => rule.restaurantName || `Restaurant #${rule.restaurantId}`

  const askRemoveRule = (rule) => setConfirm({
    title: `Send all of ${ruleName(rule)}'s orders to First Delivery?`,
    body: `This removes the rule (${rule.summary}). With auto-send on, any of its orders that has not gone yet and is not past its ready time is sent on the next check, within a minute.`,
    warning: true,
    action: 'Remove rule',
    run: () => setRule({ variables: { restaurantId: rule.restaurantId, mode: 'OFF' } })
      .then(() => done(`${ruleName(rule)}: every order goes to First Delivery`))
      .catch(fail),
  })

  return (
    <>
      <Header />
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
          <Box sx={{ maxWidth: 820 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>RushMyFood (old) → First Delivery</Typography>
              <Chip label="Temporary" size="small" sx={{ ...TONE.amber, fontWeight: 600 }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              eHungry stopped sending its delivery orders to First Delivery on Oct 2. Until it does again, this sends them for you
              using each restaurant&apos;s FD merchant id from Restaurant Readiness.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={() => refetch()} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>

        {settings && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={(
                  <Switch
                    checked={settings.autoDispatch}
                    disabled={saveState.loading || !settings.fdConfigured}
                    onChange={(e) => toggleAuto(e.target.checked)}
                  />
                )}
                label={(
                  <Typography sx={{ fontWeight: 700 }}>
                    Auto-send {settings.autoDispatch ? 'on' : 'off'}
                  </Typography>
                )}
              />
              <TextField
                size="small"
                type="number"
                label="Scheduled: minutes before ready"
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                inputProps={{ min: 0, max: 240 }}
                sx={{ width: 230 }}
              />
              <TextField
                size="small"
                type="number"
                label="ASAP: wait for eHungry (min)"
                value={grace}
                onChange={(e) => setGrace(e.target.value)}
                inputProps={{ min: 0, max: 30 }}
                sx={{ width: 210 }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={saveState.loading || (lead === String(settings.leadMinutes) && grace === String(settings.graceMinutes))}
                onClick={saveTimes}
                sx={{ textTransform: 'none' }}
              >
                Save timings
              </Button>
              <Typography variant="caption" color="text.secondary">
                Last check {settings.lastRunAt ? when(settings.lastRunAt) : 'not yet'}
                {settings.updatedBy ? ` · changed by ${settings.updatedBy} ${when(settings.updatedAt)}` : ''}
              </Typography>
            </Stack>
            {!settings.fdConfigured && (
              <Alert severity="error" sx={{ mt: 2 }}>
                First Delivery is not configured on the backend (FIRST_DELIVERY_API_KEY / FIRST_DELIVERY_BASE_URL), so nothing can be sent.
              </Alert>
            )}
            {settings.lastError && (
              <Alert severity="warning" sx={{ mt: 2 }}>Last check failed: {settings.lastError}</Alert>
            )}
          </Paper>
        )}

        {settings && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
              <Box sx={{ maxWidth: 720 }}>
                <Typography sx={{ fontWeight: 700 }}>Restaurants that deliver themselves</Typography>
                <Typography variant="caption" color="text.secondary">
                  Orders their own drivers take are never sent to First Delivery automatically.
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => setRuleDialog({ rule: null })}
                sx={{ textTransform: 'none' }}
              >
                Add restaurant
              </Button>
            </Stack>
            {rules.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                None. Every restaurant&apos;s delivery orders go to First Delivery.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
                {rules.map((rule) => (
                  <Stack key={rule.restaurantId} direction="row" alignItems="center" gap={2} py={1} flexWrap="wrap">
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{ruleName(rule)}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Own drivers: {rule.summary}
                        {rule.mode === 'DAILY' && rule.fromMinute != null ? `. Before ${clockOf(rule.fromMinute)}, First Delivery.` : ''}
                      </Typography>
                      {rule.updatedBy && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Set by {rule.updatedBy} {when(rule.updatedAt)}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={!rule.active ? 'Ended' : rule.deliversNow ? 'Own drivers now' : 'First Delivery now'}
                      sx={{ ...(!rule.active ? TONE.grey : rule.deliversNow ? TONE.green : TONE.blue), fontWeight: 600 }}
                    />
                    <Stack direction="row" gap={1}>
                      <Button size="small" disabled={setRuleState.loading} onClick={() => setRuleDialog({ rule })} sx={{ textTransform: 'none' }}>
                        Change
                      </Button>
                      <Button size="small" color="error" disabled={setRuleState.loading} onClick={() => askRemoveRule(rule)} sx={{ textTransform: 'none' }}>
                        Remove
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }} variant="scrollable" allowScrollButtonsMobile>
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={`${t.label} (${counts[t.key] || 0})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          ))}
        </Tabs>

        <Paper sx={{ overflowX: 'auto' }}>
          {loading && rows.length === 0 ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ready</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>First Delivery</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        No delivery orders here from the last 24 hours.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((r) => {
                  const s = describe(r, settings || {})
                  const d = r.dispatch || {}
                  return (
                    <TableRow key={r.orderId} hover sx={r.duplicate ? { bgcolor: '#fff1f2' } : undefined}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.linkedRestaurantName || r.restaurantName || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          #{r.externalId || r.remoteId} · placed {when(r.placedAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {money(r.total)}{r.tip ? ` · tip ${money(r.tip)}` : ''}{r.paymentDescription ? ` · ${r.paymentDescription}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{when(r.readyAt)}</Typography>
                        <Chip
                          label={r.scheduled ? 'Scheduled' : 'ASAP'}
                          size="small"
                          sx={{ mt: 0.5, height: 20, fontSize: 11, ...(r.scheduled ? TONE.blue : TONE.grey) }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2">{r.customerName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{r.customerPhone}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{r.deliveryAddress}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.label} size="small" sx={{ ...TONE[s.tone], fontWeight: 600 }} />
                        {s.note && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{s.note}</Typography>
                        )}
                        {r.duplicate && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#991b1b', fontWeight: 600 }}>
                            eHungry has also sent this order to First Delivery. Two drivers are on it, so cancel ours.
                          </Typography>
                        )}
                        {d.fdOrderId && ['SENT', 'DELIVERED', 'FD_CANCELLED', 'CANCELLED'].includes(r.state) && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {[
                              d.externalId,
                              d.dspName,
                              d.driverName && `Driver ${d.driverName}${d.driverPhone ? ` ${d.driverPhone}` : ''}`,
                              d.pickupEta && r.state === 'SENT' && `pickup ~${when(d.pickupEta)}`,
                              d.dropoffEta && r.state === 'SENT' && `drop-off ~${when(d.dropoffEta)}`,
                              `sent ${when(d.sentAt)} by ${d.sentBy}`,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        <Stack direction="row" gap={1.5}>
                          {d.trackerLink && <Link href={d.trackerLink} target="_blank" rel="noopener" variant="caption">Tracker</Link>}
                          {d.dspTrackerLink && <Link href={d.dspTrackerLink} target="_blank" rel="noopener" variant="caption">Driver tracker</Link>}
                          {d.dropoffPhoto && <Link href={d.dropoffPhoto} target="_blank" rel="noopener" variant="caption">Drop-off photo</Link>}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                          {r.canSend && (
                            <Button size="small" variant="contained" disabled={busy} onClick={() => askSend(r)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                              {r.dispatch && r.dispatch.fdOrderId ? 'Send again' : 'Send now'}
                            </Button>
                          )}
                          {r.canCancel && (
                            <Button size="small" color="error" variant="outlined" disabled={busy} onClick={() => askCancel(r)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                              Cancel driver
                            </Button>
                          )}
                          {r.canSkip && ['WAITING', 'SCHEDULED', 'DUE'].includes(r.state) && (
                            <Button size="small" disabled={busy} onClick={() => doSkip(r, true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                              Don&apos;t send
                            </Button>
                          )}
                          {r.canUnskip && (
                            <Button size="small" disabled={busy} onClick={() => doSkip(r, false)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                              Allow sending
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        {confirm && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{confirm.title}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ whiteSpace: 'pre-line', color: confirm.warning ? '#92400e' : undefined }}>
                {confirm.body}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Back</Button>
              <Button
                variant="contained"
                color={confirm.warning ? 'warning' : 'primary'}
                onClick={() => { const run = confirm.run; setConfirm(null); run() }}
                sx={{ textTransform: 'none' }}
              >
                {confirm.action}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <RuleDialog
        open={!!ruleDialog}
        rule={ruleDialog ? ruleDialog.rule : null}
        rules={rules}
        onClose={() => setRuleDialog(null)}
        onSaved={(message) => { setRuleDialog(null); done(message) }}
        onError={fail}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert> : null}
      </Snackbar>
    </>
  )
}

export default EhungryFdDispatch
